/**
 * Vehicle Real-Time Composable
 * Provides real-time vehicle updates with seamless Pinia store integration
 */

import { createClient } from '@supabase/supabase-js'
import { getCurrentInstance } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { useNotificationsStore } from '~/stores/notifications'

// Vehicle database credentials
const VEHICLE_SUPABASE_URL = 'https://tsheefvkecaervnrxvdf.supabase.co'
const VEHICLE_SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRzaGVlZnZrZWNhZXJ2bnJ4dmRmIiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTMwNDY5ODIsImV4cCI6MjAwODYyMjk4Mn0.pSnlFYXMjDt-WpH6D4DV1Km33XmFD5PBj_uwc20-J30'

// Configured seller IDs (Kevin Dennis dealerships)
const CONFIGURED_SELLER_IDS = [
  'c8997cb7-eaa2-a24c-8344-695c19dd1f92', // Kevin Dennis Volkswagen
  '54229abf-cfa5-649e-7003-b83dd4755294'  // Kevin Dennis Skoda & GMSV
]

interface RealTimeUpdate {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE'
  new?: any
  old?: any
  table: string
}

interface NotificationOptions {
  title: string
  message: string
  type: 'success' | 'info' | 'warning' | 'error'
  duration?: number
}

export const useVehicleRealtime = () => {
  // Lazy store initialization to avoid import issues
  let vehiclesStore: any = null
  let notificationsStore: any = null
  
  const initStores = () => {
    try {
      if (!vehiclesStore) {
        vehiclesStore = useVehiclesStore()
      }
      if (!notificationsStore) {
        notificationsStore = useNotificationsStore()
      }
    } catch (error) {
      console.warn('[VMS Realtime] Could not initialize stores:', error)
    }
  }
  
  // Try to get toast, but don't fail if not available
  let $toast: any = null
  try {
    const nuxtApp = useNuxtApp()
    $toast = nuxtApp.$toast
  } catch (e) {
    // Toast not available, will use console fallback
  }
  
  // State
  const isConnected = ref(false)
  const isSubscribed = ref(false)
  const connectionStatus = ref<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected')
  const lastUpdate = ref<string | null>(null)
  const updateCount = ref(0)
  const supabaseClient = ref<any>(null)
  const realtimeChannel = ref<any>(null)
  
  // Statistics
  const stats = ref({
    vehiclesAdded: 0,
    vehiclesUpdated: 0,
    vehiclesRemoved: 0,
    priceUpdates: 0,
    totalUpdates: 0
  })

  /**
   * Initialize Supabase client
   */
  function initializeClient() {
    if (!supabaseClient.value) {
      supabaseClient.value = createClient(VEHICLE_SUPABASE_URL, VEHICLE_SUPABASE_KEY)
      console.log('[VMS Realtime] Supabase client initialized')
    }
    return supabaseClient.value
  }

  /**
   * Subscribe to real-time vehicle updates
   */
  async function subscribeToVehicleUpdates() {
    if (isSubscribed.value || !process.client) return

    // Initialize stores
    initStores()

    try {
      connectionStatus.value = 'connecting'
      const client = initializeClient()

      // Create channel for vehicle updates
      realtimeChannel.value = client
        .channel('vehicle-realtime-updates')
        .on(
          'postgres_changes',
          {
            event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
            schema: 'public',
            table: 'vehicles',
            filter: `seller_id=in.(${CONFIGURED_SELLER_IDS.join(',')})`
          },
          (payload: any) => {
            console.log('[VMS Realtime] Vehicle update received:', payload)
            handleVehicleUpdate(payload)
          }
        )
        .subscribe((status: string) => {
          console.log('[VMS Realtime] Subscription status:', status)
          
          if (status === 'SUBSCRIBED') {
            isSubscribed.value = true
            isConnected.value = true
            connectionStatus.value = 'connected'
            showNotification({
              title: '🚀 Real-time Updates Active',
              message: 'You will now receive live vehicle updates',
              type: 'success',
              duration: 3000
            })
          } else if (status === 'CHANNEL_ERROR') {
            connectionStatus.value = 'error'
            isConnected.value = false
            console.error('[VMS Realtime] Channel error')
          }
        })

      console.log('[VMS Realtime] Subscribed to vehicle updates for sellers:', CONFIGURED_SELLER_IDS)

    } catch (error) {
      console.error('[VMS Realtime] Failed to subscribe:', error)
      connectionStatus.value = 'error'
      isConnected.value = false
      
      showNotification({
        title: '❌ Real-time Connection Failed',
        message: 'Could not connect to live updates',
        type: 'error',
        duration: 5000
      })
    }
  }

  /**
   * Handle real-time vehicle updates
   */
  function handleVehicleUpdate(payload: RealTimeUpdate) {
    // Initialize stores
    initStores()
    
    const { eventType, new: newVehicle, old: oldVehicle } = payload
    
    lastUpdate.value = new Date().toISOString()
    updateCount.value++
    stats.value.totalUpdates++

    switch (eventType) {
      case 'INSERT':
        handleVehicleInsert(newVehicle)
        break
      case 'UPDATE':
        handleVehicleUpdate_Internal(newVehicle, oldVehicle)
        break
      case 'DELETE':
        handleVehicleDelete(oldVehicle)
        break
    }

    // Refresh available filters after any change
    vehiclesStore.initializeAvailableFilters()
  }

  /**
   * Handle new vehicle insertion
   */
  function handleVehicleInsert(vehicle: any) {
    if (!vehicle || vehicle.sale_status !== 'For Sale') return

    // If an INSERT arrives for a vehicle we already have (e.g., due to timing/race conditions),
    // update the existing entry instead of duplicating it.
    const candidateId = vehicle.identifier || vehicle.id || vehicle.stock_number
    const existingIndex = findVehicleIndex(candidateId)

    const transformedVehicle = transformDatabaseVehicle(vehicle)

    if (existingIndex !== -1) {
      // Treat as an update-in-place to avoid duplicate entries
      vehiclesStore.vehicles[existingIndex] = transformedVehicle
      stats.value.vehiclesUpdated++
      console.log('[VMS Realtime] INSERT matched existing vehicle. Updated in place to avoid duplicate:', transformedVehicle.title)
      return
    }

    stats.value.vehiclesAdded++

    // Add to store (prepend for latest-first ordering)
    vehiclesStore.vehicles.unshift(transformedVehicle)
    
    // Add to notifications store
    if (notificationsStore) {
      notificationsStore.addVehicleNotification(
        'New Vehicle Added',
        `${transformedVehicle.title} is now available`,
        transformedVehicle.id,
        transformedVehicle.title,
        'new_vehicle',
        'success'
      )
    }
    
    showNotification({
      title: '🚗 New Vehicle Added',
      message: `${transformedVehicle.title} is now available`,
      type: 'success',
      duration: 4000
    })
    
    console.log('[VMS Realtime] Vehicle inserted:', transformedVehicle.title)
  }

  /**
   * Handle vehicle updates
   */
  function handleVehicleUpdate_Internal(newVehicle: any, oldVehicle: any) {
    if (!newVehicle) return

    const vehicleIndex = findVehicleIndex(newVehicle.identifier || newVehicle.id)
    
    if (newVehicle.sale_status === 'For Sale') {
      stats.value.vehiclesUpdated++
      const transformedVehicle = transformDatabaseVehicle(newVehicle)
      
      if (vehicleIndex !== -1) {
        // Update existing vehicle
        vehiclesStore.vehicles[vehicleIndex] = transformedVehicle
        
        // Check for significant changes
        checkForSignificantUpdates(newVehicle, oldVehicle, transformedVehicle)
      } else {
        // Vehicle wasn't in our store before, add it
        vehiclesStore.vehicles.unshift(transformedVehicle)
        
        showNotification({
          title: '🔄 Vehicle Updated',
          message: `${transformedVehicle.title} has been updated`,
          type: 'info',
          duration: 3000
        })
      }
    } else {
      // Vehicle no longer for sale
      if (vehicleIndex !== -1) {
        const vehicle = vehiclesStore.vehicles[vehicleIndex]
        const vehicleTitle = vehicle.title
        const vehicleId = vehicle.id
        
        vehiclesStore.vehicles.splice(vehicleIndex, 1)
        stats.value.vehiclesRemoved++
        
        // Add to notifications store
        if (notificationsStore) {
          notificationsStore.addVehicleNotification(
            'Vehicle No Longer Available',
            `${vehicleTitle} has been sold or removed`,
            vehicleId,
            vehicleTitle,
            'vehicle_removed',
            'warning'
          )
        }
        
        showNotification({
          title: '❌ Vehicle No Longer Available',
          message: `${vehicleTitle} has been sold or removed`,
          type: 'warning',
          duration: 4000
        })
      }
    }
    
    console.log('[VMS Realtime] Vehicle updated:', newVehicle.title)
  }

  /**
   * Handle vehicle deletion
   */
  function handleVehicleDelete(vehicle: any) {
    if (!vehicle) return

    const vehicleIndex = findVehicleIndex(vehicle.identifier || vehicle.id)
    
    if (vehicleIndex !== -1) {
      const vehicleToDelete = vehiclesStore.vehicles[vehicleIndex]
      const vehicleTitle = vehicleToDelete.title
      const vehicleId = vehicleToDelete.id
      
      vehiclesStore.vehicles.splice(vehicleIndex, 1)
      stats.value.vehiclesRemoved++
      
      // Add to notifications store
      if (notificationsStore) {
        notificationsStore.addVehicleNotification(
          'Vehicle Removed',
          `${vehicleTitle} has been deleted`,
          vehicleId,
          vehicleTitle,
          'vehicle_removed',
          'warning'
        )
      }
      
      showNotification({
        title: '🗑️ Vehicle Removed',
        message: `${vehicleTitle} has been deleted`,
        type: 'warning',
        duration: 4000
      })
      
      console.log('[VMS Realtime] Vehicle deleted:', vehicleTitle)
    }
  }

  /**
   * Check for significant updates that need notifications
   */
  function checkForSignificantUpdates(newVehicle: any, oldVehicle: any, transformedVehicle: any) {
    // Price changes
    if (oldVehicle.dap_price !== newVehicle.dap_price) {
      stats.value.priceUpdates++
      
      const oldPrice = parseFloat(oldVehicle.dap_price || '0')
      const newPrice = parseFloat(newVehicle.dap_price || '0')
      const priceChange = newPrice - oldPrice
      const isReduction = priceChange < 0
      
      // Add to notifications store
      if (notificationsStore) {
        notificationsStore.addVehicleNotification(
          isReduction ? 'Price Reduced!' : 'Price Updated',
          `${transformedVehicle.title}: ${isReduction ? 'Now' : 'Updated to'} $${newPrice.toLocaleString()}`,
          transformedVehicle.id,
          transformedVehicle.title,
          'price_change',
          isReduction ? 'success' : 'info'
        )
      }
      
      showNotification({
        title: isReduction ? '💰 Price Reduced!' : '📈 Price Updated',
        message: `${transformedVehicle.title}: ${isReduction ? 'Now' : 'Updated to'} $${newPrice.toLocaleString()}`,
        type: isReduction ? 'success' : 'info',
        duration: 5000
      })
    }

    // Status changes (e.g., condition changes)
    if (oldVehicle.listing_type !== newVehicle.listing_type) {
      showNotification({
        title: '🔄 Vehicle Condition Updated',
        message: `${transformedVehicle.title} is now listed as ${newVehicle.listing_type}`,
        type: 'info',
        duration: 3000
      })
    }

    // Photo updates
    if (oldVehicle.main_photo_url !== newVehicle.main_photo_url) {
      showNotification({
        title: '📸 New Photos Available',
        message: `${transformedVehicle.title} has updated photos`,
        type: 'info',
        duration: 3000
      })
    }
  }

  /**
   * Find vehicle index in store
   */
  function findVehicleIndex(vehicleId: string): number {
    return vehiclesStore.vehicles.findIndex(v => 
      v.id === vehicleId || 
      v.stockid === vehicleId ||
      v.identifier === vehicleId
    )
  }

  /**
   * Transform database vehicle to frontend format
   */
  function transformDatabaseVehicle(dbVehicle: any) {
    return {
      id: dbVehicle.identifier || dbVehicle.id,
      stockid: dbVehicle.stock_number,
      slug: slugify(dbVehicle.title || ''),
      title: dbVehicle.title,
      description: dbVehicle.description,
      price: parseFloat(dbVehicle.dap_price || '0'),
      kms: dbVehicle.odometer_reading,
      thumb: dbVehicle.main_photo_url,
      photos: dbVehicle.photos || [],
      make: {
        value: dbVehicle.make ? [slugify(dbVehicle.make)] : [],
        displayValue: dbVehicle.make ? [dbVehicle.make] : []
      },
      model: {
        value: dbVehicle.model ? [slugify(dbVehicle.model)] : [],
        displayValue: dbVehicle.model ? [dbVehicle.model] : [],
        displayMake: dbVehicle.make ? [{
          value: [slugify(dbVehicle.make)],
          displayValue: [dbVehicle.make]
        }] : [],
        displayBody: dbVehicle.body_style ? [dbVehicle.body_style] : []
      },
      badge: {
        value: dbVehicle.badge ? [slugify(dbVehicle.badge)] : [],
        displayValue: dbVehicle.badge ? [dbVehicle.badge] : [],
        displayMake: dbVehicle.make ? [{
          value: [slugify(dbVehicle.make)],
          displayValue: [dbVehicle.make]
        }] : []
      },
      condition: {
        value: dbVehicle.listing_type ? [slugify(dbVehicle.listing_type)] : [],
        displayValue: dbVehicle.listing_type ? [dbVehicle.listing_type] : []
      },
      body: {
        value: dbVehicle.body_style ? [slugify(dbVehicle.body_style)] : [],
        displayValue: dbVehicle.body_style ? [dbVehicle.body_style] : []
      },
      transmission: {
        value: dbVehicle.transmission ? [slugify(dbVehicle.transmission)] : [],
        displayValue: dbVehicle.transmission ? [dbVehicle.transmission] : []
      },
      drivetrain: {
        value: dbVehicle.drive ? [slugify(dbVehicle.drive)] : [],
        displayValue: dbVehicle.drive ? [dbVehicle.drive] : []
      },
      fuel: {
        value: dbVehicle.fuel_type ? [slugify(dbVehicle.fuel_type)] : [],
        displayValue: dbVehicle.fuel_type ? [dbVehicle.fuel_type] : []
      },
      doors: {
        value: dbVehicle.doors ? [dbVehicle.doors.toString()] : [],
        displayValue: dbVehicle.doors ? [dbVehicle.doors.toString()] : []
      },
      seats: {
        value: dbVehicle.seats ? [dbVehicle.seats.toString()] : [],
        displayValue: dbVehicle.seats ? [dbVehicle.seats.toString()] : []
      },
      year: {
        value: dbVehicle.build_year ? [dbVehicle.build_year.toString()] : [],
        displayValue: dbVehicle.build_year ? [dbVehicle.build_year.toString()] : []
      },
      colour: {
        value: (dbVehicle.exterior_colour_generic || dbVehicle.exterior_colour_name)
          ? [slugify(dbVehicle.exterior_colour_generic || dbVehicle.exterior_colour_name)]
          : [],
        displayValue: (dbVehicle.exterior_colour_generic || dbVehicle.exterior_colour_name)
          ? [dbVehicle.exterior_colour_generic || dbVehicle.exterior_colour_name]
          : []
      },
      vin: dbVehicle.vin,
      rego: dbVehicle.registration_number,
      sellername: dbVehicle.seller_name,
      latitude: dbVehicle.latitude,
      longitude: dbVehicle.longitude,
      address: dbVehicle.address,
      attributes: dbVehicle.attributes,
      search_keywords: `${dbVehicle.title} ${dbVehicle.make} ${dbVehicle.model} ${dbVehicle.stock_number}`.toLowerCase(),
      isNew: dbVehicle.listing_type === 'New',
      sourceName: 'VMS Database (Real-time)',
      // Use DB-provided last_modified_utc when available to keep indicators accurate
      lastUpdated: dbVehicle.last_modified_utc || new Date().toISOString()
    }
  }

  /**
   * Show notification
   */
  function showNotification(options: NotificationOptions) {
    if (process.client) {
      // Initialize stores
      initStores()
      
      // Store notification in the notifications store
      if (notificationsStore) {
        if (options.title.includes('Real-time') || options.title.includes('Connection')) {
          // Connection-related notifications
          notificationsStore.addConnectionNotification(
            options.title,
            options.message,
            options.type
          )
        } else {
          // System notifications
          notificationsStore.addSystemNotification(
            options.title,
            options.message,
            options.type
          )
        }
      }

      // Try to use your existing toast system
      if ($toast) {
        $toast.add({
          title: options.title,
          description: options.message,
          color: getToastColor(options.type),
          timeout: options.duration || 4000
        })
      } else {
        // Fallback to console for now
        console.log(`[VMS Notification] ${options.title}: ${options.message}`)
        
        // Could also use browser notification API
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(options.title, {
            body: options.message,
            icon: '/favicon.ico'
          })
        }
      }
    }
  }

  /**
   * Get toast color based on type
   */
  function getToastColor(type: string) {
    switch (type) {
      case 'success': return 'green'
      case 'error': return 'red'
      case 'warning': return 'orange'
      case 'info': 
      default: return 'blue'
    }
  }

  /**
   * Unsubscribe from real-time updates
   */
  function unsubscribeFromVehicleUpdates() {
    if (realtimeChannel.value) {
      realtimeChannel.value.unsubscribe()
      realtimeChannel.value = null
    }
    
    isSubscribed.value = false
    isConnected.value = false
    connectionStatus.value = 'disconnected'
    
    console.log('[VMS Realtime] Unsubscribed from vehicle updates')
    
    showNotification({
      title: '🔌 Real-time Updates Disabled',
      message: 'Live vehicle updates have been turned off',
      type: 'info',
      duration: 2000
    })
  }

  /**
   * Request notification permissions
   */
  async function requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }
    return Notification.permission === 'granted'
  }

  /**
   * Reset statistics
   */
  function resetStats() {
    stats.value = {
      vehiclesAdded: 0,
      vehiclesUpdated: 0,
      vehiclesRemoved: 0,
      priceUpdates: 0,
      totalUpdates: 0
    }
    updateCount.value = 0
  }

  /**
   * Utility: Slugify text
   */
  function slugify(text: string): string {
    if (!text) return ''
    return text
      .toLowerCase()
      .replace(/[^\w\s-]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  // Cleanup on unmount - only if we're in a component context
  if (process.client && getCurrentInstance()) {
    onUnmounted(() => {
      unsubscribeFromVehicleUpdates()
    })
  }

  return {
    // State
    isConnected: readonly(isConnected),
    isSubscribed: readonly(isSubscribed),
    connectionStatus: readonly(connectionStatus),
    lastUpdate: readonly(lastUpdate),
    updateCount: readonly(updateCount),
    stats: readonly(stats),
    
    // Methods
    subscribeToVehicleUpdates,
    unsubscribeFromVehicleUpdates,
    requestNotificationPermission,
    resetStats,
    
    // Utility
    CONFIGURED_SELLER_IDS
  }
}

/**
 * Utility: Slugify text (exported for reuse)
 */
export function slugify(text: string): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .replace(/[^\w\s-]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}